import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from 'react-query';
import {
  BadgeCheck,
  CalendarClock,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  Phone,
  Save,
  ShieldCheck,
  Store,
  UserRound,
} from 'lucide-react';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import Skeleton, { SkeletonText } from '../../components/common/Skeleton';
import TopProgressBar from '../../components/common/TopProgressBar';

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.errors?.[0]?.message ||
  error?.response?.data?.message ||
  fallbackMessage;

const formatDateTime = (value) => {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getStoreLabel = (profile) => {
  if (!profile?.storeId) return 'All Store';
  return profile.storeName || profile.storeCode || `Store ${profile.storeId}`;
};

const ProfileField = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-gray-200 bg-white p-4">
    <div className="flex items-start gap-3">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p className="mt-1 break-words text-sm font-semibold text-gray-900">{value || 'Not available'}</p>
      </div>
    </div>
  </div>
);

const ProfileSkeleton = () => (
  <div className="space-y-4" aria-hidden="true">
    <div className="rounded-md border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-4">
        <Skeleton className="h-14 w-14 rounded-full" />
        <div className="min-w-0 flex-1">
          <Skeleton className="h-5 w-48 max-w-full" />
          <SkeletonText className="mt-3 w-72 max-w-full" />
        </div>
      </div>
    </div>
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="rounded-md border border-gray-200 bg-white p-4">
          <Skeleton className="h-4 w-24" />
          <SkeletonText className="mt-4 w-40" />
        </div>
      ))}
    </div>
  </div>
);

const PasswordInput = ({
  id,
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
  autoComplete,
}) => (
  <div>
    <label htmlFor={id} className="label">
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        name={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className="input pr-11"
        required
      />
      <button
        type="button"
        onClick={onToggleVisible}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        title={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

const Profile = () => {
  const { user, setRequiresPasswordReset } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const profileQuery = useQuery('currentUserProfile', () => authAPI.getMe(), {
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const profile = useMemo(
    () => profileQuery.data?.data?.data || user || {},
    [profileQuery.data, user]
  );

  const initials = useMemo(() => {
    const name = String(profile?.name || profile?.email || 'U').trim();
    return name.charAt(0).toUpperCase();
  }, [profile]);

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((previous) => ({ ...previous, [field]: !previous[field] }));
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (form.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    if (form.currentPassword === form.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setIsSubmitting(true);
    try {
      await authAPI.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });

      setForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setRequiresPasswordReset(false);
      await profileQuery.refetch();
      toast.success('Password updated successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Password update failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { key: 'profile', label: 'Profile', icon: UserRound },
    { key: 'password', label: 'Edit Password', icon: LockKeyhole },
  ];

  return (
    <div className="space-y-5">
      <TopProgressBar active={profileQuery.isFetching || isSubmitting} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="mt-1 text-sm text-gray-500">Account details and password settings.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-semibold transition-colors ${
                active
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' ? (
        profileQuery.isLoading && !user ? (
          <ProfileSkeleton />
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-gray-200 bg-white p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xl font-bold text-primary-700">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-bold text-gray-900">{profile?.name || 'Not available'}</h2>
                    <p className="truncate text-sm text-gray-500">{profile?.email || 'Not available'}</p>
                  </div>
                </div>
                <span className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold ${
                  profile?.isActive === false
                    ? 'bg-red-100 text-red-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {profile?.isActive === false ? 'Inactive' : 'Active'}
                </span>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <ProfileField icon={ShieldCheck} label="Role" value={profile?.role} />
              <ProfileField icon={BadgeCheck} label="Designation" value={profile?.designation} />
              <ProfileField icon={Store} label="Assigned Store" value={getStoreLabel(profile)} />
              <ProfileField icon={Mail} label="Email" value={profile?.email} />
              <ProfileField icon={Phone} label="Phone" value={profile?.phone} />
              <ProfileField icon={CalendarClock} label="Last Login" value={formatDateTime(profile?.lastLogin)} />
            </div>
          </div>
        )
      ) : (
        <form onSubmit={handlePasswordSubmit} className="max-w-xl space-y-5 rounded-md border border-gray-200 bg-white p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Password</h2>
            <p className="mt-1 text-sm text-gray-500">Use a password that is different from your current one.</p>
          </div>

          <PasswordInput
            id="currentPassword"
            label="Current Password"
            value={form.currentPassword}
            onChange={handlePasswordChange}
            visible={showPassword.currentPassword}
            onToggleVisible={() => togglePasswordVisibility('currentPassword')}
            autoComplete="current-password"
          />

          <PasswordInput
            id="newPassword"
            label="New Password"
            value={form.newPassword}
            onChange={handlePasswordChange}
            visible={showPassword.newPassword}
            onToggleVisible={() => togglePasswordVisibility('newPassword')}
            autoComplete="new-password"
          />

          <PasswordInput
            id="confirmPassword"
            label="Confirm New Password"
            value={form.confirmPassword}
            onChange={handlePasswordChange}
            visible={showPassword.confirmPassword}
            onToggleVisible={() => togglePasswordVisibility('confirmPassword')}
            autoComplete="new-password"
          />

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSubmitting ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Profile;
